import type { SerializedCombinedMeter } from '@enmon/commons/lib/enmon';
import { INVENTORIZATION_BLOCK_IDS } from '@/features/inventorization/domain/inventorization';
import { INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS } from '@/features/inventorization/domain/review/inventorize-review-field-ids';
import { loadRawLocationMeters } from '@/features/location-bootstrap/location-bootstrap-requests';
import {
  FIELD_SYNC_TRANSPORT_STATUSES,
  type FieldSyncTransport,
  FieldSyncTransportError,
  type FieldSyncTransportResult,
  type FieldSyncTransportStatus,
} from '@/features/sync/engine/field-sync-transport';
import {
  createInventorizationMultipartPayload,
  type FloorplanBlobReader,
  type PhotoBlobReader,
} from '@/features/sync/engine/http-inventorization-multipart';
import { resolveInventorizationMeterIds } from '@/features/sync/engine/inventorization-meter-lookup';
import type { InventorizationSyncPayload } from '@/features/sync/engine/inventorization-sync-payload';
import {
  createInventoryAxiosClient,
  createLocationApiKeyAxiosClient,
  type InventoryAxiosClient,
} from '@/shared/api/default-axios-client';
import type { ValidationIssue } from '@/shared/capture/capture-session';
import { fieldDatabase } from '@/shared/db/default-field-database';
import { FloorplanBlobRepository } from '@/shared/db/repositories/floorplan-blob-repository';
import { PhotoBlobRepository } from '@/shared/db/repositories/photo-blob-repository';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';
import { normalizeSearchField } from '@/shared/meter-identification';

interface FieldSyncValidationError {
  message?: string;
  path?: string | Array<string | number>;
  type?: string;
}

interface FieldSyncResponseBody {
  _id?: string;
  data?: unknown;
  code?: string;
  details?: { originalDetails?: FieldSyncValidationError[] };
  errorCode?: string;
  message?: string;
  error?: {
    message?: string;
    details?: { errors?: FieldSyncValidationError[] };
  };
  id?: number | string;
  remoteEntityIds?: FieldSyncTransportResult['remoteEntityIds'];
  status?: FieldSyncTransportStatus;
  validationErrors?: ValidationIssue[];
}

export const INVENTORIZATION_SYNC_TIMEOUT_MS = 30_000;
const METER_LISTING_REQUEST_TIMEOUT_MS = 20_000;
const METER_LISTING_MAX_AGE_MS = 60_000;

interface CachedMeterListing {
  loadedAt: number;
  meters: Promise<SerializedCombinedMeter[]>;
}

export class HttpFieldSyncTransport implements FieldSyncTransport {
  private readonly axios: InventoryAxiosClient;
  private readonly floorplanBlobs: FloorplanBlobReader;
  private readonly meterListings = new Map<string, CachedMeterListing>();
  private readonly photoBlobs: PhotoBlobReader;

  constructor(input: {
    axiosClient?: InventoryAxiosClient;
    baseUrl?: string;
    floorplanBlobs?: FloorplanBlobReader;
    photoBlobs?: PhotoBlobReader;
  }) {
    this.axios = input.axiosClient ?? createInventoryAxiosClient({ baseUrl: input.baseUrl });
    this.floorplanBlobs = input.floorplanBlobs ?? new FloorplanBlobRepository(fieldDatabase);
    this.photoBlobs = input.photoBlobs ?? new PhotoBlobRepository(fieldDatabase);
  }

  async submitInventorization(payload: InventorizationSyncPayload): Promise<FieldSyncTransportResult> {
    if (!this.axios.defaults.baseURL) {
      throw new Error('API base URL is required for HTTP sync.');
    }

    if (!payload.locationApiKey?.trim()) {
      throw new Error('Location API key is required for HTTP sync.');
    }

    const axios = createLocationApiKeyAxiosClient({
      axiosClient: this.axios,
      locationApiKey: payload.locationApiKey,
      refreshCache: true,
    });
    if (payload.locationId == null) {
      return meterLookupFailure('METER_LOOKUP_FAILED');
    }

    const listingKey = `${payload.locationId}:${payload.locationApiKey}`;
    let meters: SerializedCombinedMeter[];
    try {
      meters = await this.loadMeterListing(listingKey, axios, payload.locationId);
    } catch (error) {
      throw new FieldSyncTransportError('METER_LOOKUP_FAILED', { cause: error });
    }
    const meterIds = resolveInventorizationMeterIds(payload, meters);
    if (!meterIds) {
      return meterLookupFailure('METER_IDENTITY_CONFLICT');
    }

    let result: FieldSyncTransportResult | undefined;
    try {
      result = await this.submit(axios, payload, meterIds);
      return result;
    } finally {
      if (!this.isListingStillValid(result, payload, meterIds, meters)) {
        this.meterListings.delete(listingKey);
      }
    }
  }

  private loadMeterListing(key: string, axios: InventoryAxiosClient, locationId: number) {
    const cached = this.meterListings.get(key);
    if (cached && Date.now() - cached.loadedAt < METER_LISTING_MAX_AGE_MS) {
      return cached.meters;
    }

    const meters = loadRawLocationMeters({
      axios,
      locationId,
      requestTimeoutMs: METER_LISTING_REQUEST_TIMEOUT_MS,
    });
    this.meterListings.set(key, { loadedAt: Date.now(), meters });
    meters.catch(() => {
      if (this.meterListings.get(key)?.meters === meters) this.meterListings.delete(key);
    });

    return meters;
  }

  // Keep the listing only when the submission cannot have added a meter or changed a looked-up identity.
  private isListingStillValid(
    result: FieldSyncTransportResult | undefined,
    payload: InventorizationSyncPayload,
    meterIds: NonNullable<ReturnType<typeof resolveInventorizationMeterIds>>,
    meters: SerializedCombinedMeter[],
  ) {
    if (
      result?.status === FIELD_SYNC_TRANSPORT_STATUSES.rejected ||
      result?.status === FIELD_SYNC_TRANSPORT_STATUSES.conflict
    ) {
      return true;
    }

    const known = meterIds._id ? meters.find(meter => meter._id === meterIds._id) : undefined;
    return (
      result?.status === FIELD_SYNC_TRANSPORT_STATUSES.accepted &&
      known !== undefined &&
      normalizeSearchField(known.apiKey) === normalizeSearchField(payload.identity.apiKey) &&
      normalizeSearchField(known.serial) === normalizeSearchField(payload.identity.serial) &&
      known.commodity === payload.identity.commodity
    );
  }

  private async submit(
    axios: InventoryAxiosClient,
    payload: InventorizationSyncPayload,
    meterIds: NonNullable<ReturnType<typeof resolveInventorizationMeterIds>>,
  ): Promise<FieldSyncTransportResult> {
    const response = await axios.put<FieldSyncResponseBody>(
      '/inventory-meters/inventorize',
      await createInventorizationMultipartPayload({
        floorplanBlobs: this.floorplanBlobs,
        payload: { ...payload, ...meterIds },
        photoBlobs: this.photoBlobs,
      }),
      {
        timeout: INVENTORIZATION_SYNC_TIMEOUT_MS,
        validateStatus: () => true,
      },
    );
    const body = readResponseBody(response.data, response.status);

    if (response.status >= 200 && response.status < 300) {
      return {
        errorCode: body.errorCode,
        remoteEntityIds: { ...meterIds, ...body.remoteEntityIds },
        responseStatus: response.status,
        status: body.status ?? FIELD_SYNC_TRANSPORT_STATUSES.accepted,
        validationErrors: body.validationErrors,
      };
    }

    return {
      errorCode: body.errorCode ?? (response.status === 409 ? 'CONFLICT' : `HTTP_${response.status}`),
      responseStatus: response.status,
      status:
        response.status === 409
          ? FIELD_SYNC_TRANSPORT_STATUSES.conflict
          : response.status === 422
            ? FIELD_SYNC_TRANSPORT_STATUSES.rejected
            : FIELD_SYNC_TRANSPORT_STATUSES.partial,
      validationErrors: body.validationErrors,
    };
  }
}

function meterLookupFailure(errorCode: 'METER_LOOKUP_FAILED' | 'METER_IDENTITY_CONFLICT'): FieldSyncTransportResult {
  const message =
    errorCode === 'METER_LOOKUP_FAILED'
      ? translateInventoryText(
          'app.inventory.sync.error.meterLookupFailed',
          'Could not check whether this meter already exists in ENMON. Nothing was sent. Your data and photos are saved on this device. Retry synchronization when connected.',
        )
      : translateInventoryText(
          'app.inventory.sync.error.meterIdentityConflict',
          'This meter could not be safely matched to an ENMON record. Check the QR/API key, serial number and selected location. Nothing was sent; your data and photos are saved on this device. If the details are correct, contact support.',
        );
  return {
    errorCode,
    status: FIELD_SYNC_TRANSPORT_STATUSES.rejected,
    validationErrors: [{ code: errorCode, message, severity: 'blocking' }],
  };
}

function readResponseBody(body: FieldSyncResponseBody | undefined, status: number): FieldSyncResponseBody {
  const responseBody = {
    ...body,
    errorCode: body?.errorCode || body?.code,
  };

  return {
    ...responseBody,
    remoteEntityIds:
      responseBody.remoteEntityIds ?? (status >= 200 && status < 300 ? readRemoteEntityIds(responseBody) : undefined),
    validationErrors: readValidationErrors(responseBody, status),
  };
}

function readRemoteEntityIds(body: FieldSyncResponseBody) {
  const id = typeof body.id === 'number' ? body.id : undefined;
  const _id = body._id;

  return {
    ...(_id ? { _id } : {}),
    ...(id ? { id } : {}),
  };
}

function readValidationErrors(body: FieldSyncResponseBody, status: number): ValidationIssue[] | undefined {
  if (body.validationErrors?.length || status < 400) {
    return body.validationErrors;
  }

  const errorCode = body.errorCode ?? `HTTP_${status}`;
  const errors = [body.error?.details?.errors, body.details?.originalDetails].flatMap(errors =>
    Array.isArray(errors) ? errors : [],
  );
  const issues: ValidationIssue[] = errors.flatMap((error, index) => {
    if (typeof error?.message !== 'string' || !error.message.trim()) {
      return [];
    }
    const path = Array.isArray(error.path) ? error.path.join('.') : typeof error.path === 'string' ? error.path : '';
    const isApiKey = path === 'apiKey';
    const isDuplicateApiKey = isApiKey && error.type === 'custom.exists';
    return [
      {
        code: isDuplicateApiKey ? 'METER_API_KEY_ALREADY_EXISTS' : `${errorCode}_${index}`,
        ...(isApiKey
          ? {
              blockId: INVENTORIZATION_BLOCK_IDS.identity,
              fieldId: INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.apiKey,
            }
          : {}),
        message: isDuplicateApiKey
          ? translateInventoryText(
              'app.inventory.sync.error.duplicateApiKey',
              'A meter with this QR/API key already exists in ENMON. Check the code against the meter label. If it is correct, contact support to check the meter assignment. Retrying without a change will not resolve this error.',
            )
          : path
            ? `${path}: ${error.message}`
            : error.message,
        severity: 'blocking' as const,
      },
    ];
  });

  if (issues.length) {
    return issues;
  }

  const message = body.error?.message ?? body.message;
  return [
    {
      code: errorCode,
      message:
        typeof message === 'string' && message.trim()
          ? message
          : translateInventoryText(
              'app.inventory.sync.error.rejected',
              'The server rejected this meter ({code}). Retry synchronization; if it fails again, contact support with the meter name and this code.',
              { code: errorCode },
            ),
      severity: 'blocking',
    },
  ];
}
